import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { State, process } from '@progress/kendo-data-query';
import { BehaviorSubject, combineLatest, forkJoin, iif, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CampaignService } from '../core/data/campaign-service';
import { StudentProfileService } from '../core/data/student-profile.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ss-student-campaign-grids',
    templateUrl: './student-campaign-grids.component.html',
    styles: [
        `
            .pb-35 {
                padding-bottom: 35px;
            }
            .btn-right {
                display: flex;
                align-items: flex-end;
                justify-content: flex-end;
            }
            dd > span > u {
                word-break: break-all;
            }
        `
    ]
})
export class StudentCampaignGridsComponent {

    private completedState$ = new BehaviorSubject({
        skip: 0,
        take: 10,
    } as State);

    private _viewConfig = new BehaviorSubject<{ schoolViewer: boolean, userId: string | null }>(
        {
            schoolViewer: false,
            userId: null
        }
    );
    @Input()
    set viewConfig(value: { schoolViewer: boolean, userId: string }) {
        this._viewConfig.next(value);
    }

    vm$ = this._viewConfig.pipe(
        switchMap(user =>
            iif(() => !user.schoolViewer,
                combineLatest([this.campaignService.getStudentCampaigns(), this.completedState$]),
                combineLatest([this.campaignService.getProfileStudentCampaigns(user.userId), this.completedState$])
            )
                .pipe(
                    switchMap(([data, completedState]) => {
                        const draftOpenCampaign = data.find(x => x.status === 'Open' || x.status === 'Draft') || null;

                        const completedArchivedCampaigns = data.filter(x => x.status === 'Completed' || x.status === 'Archived');

                        return forkJoin([
                            of(completedArchivedCampaigns),
                            of(completedState),
                            draftOpenCampaign ? of([{ ...draftOpenCampaign }]) : of([])
                        ]);
                    }),
                    map(([data, completedState, draftOpenCampaigns]) =>
                    (
                        {
                            activeGridData: process(draftOpenCampaigns, {
                                skip: 0,
                                take: 10,
                            } as State),
                            completedGridData: process(data, completedState),
                            completed: {
                                state: completedState
                            }
                        })
                    )
                ))
    );
    constructor(
        private campaignService: CampaignService,
        private router: Router,
        private studentProfileService: StudentProfileService) { }

    goToCampaign = (campaignId: number) => window.open(this.router.createUrlTree(['/campaign', campaignId]).toString(), '_blank');

    editCampaign = (campaignId: number, verifiedStudentId: string) => {
        const val = this._viewConfig.getValue();
        if (val.schoolViewer) {
            this.router.navigate([
                `school/campaigns/edit/${campaignId}/${verifiedStudentId}`
            ]).then(() => {
                this.studentProfileService.verifiedStudentId$.next(null);
            });
        }
        else
            this.router.navigate([
                `student/campaigns/edit/${campaignId}/${verifiedStudentId}`
            ]);
    }

    completedDataStateChange = (currentState: State) =>
        this.completedState$.next(currentState)
}
